<app-page
  pageTitle="Podešavanja"
  pageDescription="Konfiguracija sistemskih opcija i pravila"
  [showDivider]="true"
>
  <div class="settings-container mt-5">
    <div class="settings-form">
      <!-- Admins -->
      <section class="settings-section">
        <div class="settings-section__label">
          <div class="text-sm-semibold text-gray-900">Upravljanje adminima</div>
        </div>
        <div class="settings-section__content settings-section__content--wide">
          <div class="admins-card">
            <div class="admins-header">
              <div class="text-xs-semibold text-gray-400 admins-name-col">IME I PREZIME</div>
              <div class="text-xs-semibold text-gray-400 admins-position-col">POZICIJA</div>
              <div class="admins-actions-col"></div>
            </div>

            @for (admin of admins(); track admin.id) {
              <div class="admins-row">
                <div class="flex align-items-center gap-3 admins-name-col">
                  <div class="admin-avatar">
                    <app-user-circle stroke="#7E838F" width="24px" height="24px"></app-user-circle>
                  </div>
                  <div class="flex flex-column admin-identity">
                    <div class="text-sm-medium text-gray-900">{{ adminDisplayName(admin) }}</div>
                    <div class="text-sm-medium text-gray-500">{{ admin.email }}</div>
                  </div>
                </div>
                <div class="admins-position-col">
                  <div class="text-sm-medium text-gray-900">Admin</div>
                </div>
                <div class="admins-actions-col flex justify-content-end">
                  @if (admin.id !== currentUserId) {
                    <app-button
                      [btnSize]="ButtonSize.SM"
                      [btnSeverity]="ButtonSeverity.TERTIARY"
                      [isDestructive]="true"
                      btnClass="icon-only"
                      (btnClicked)="adminToDeactivate.set(admin)"
                    >
                      <app-trash-01 stroke="#F04438" width="20px" height="20px"></app-trash-01>
                    </app-button>
                  }
                </div>
              </div>
            }
          </div>

          <app-button
            [btnSeverity]="ButtonSeverity.LINK"
            (btnClicked)="addAdmin()"
          >
            <app-plus stroke="#2970FF" width="20px" height="20px"></app-plus>
            <span>Dodaj admina</span>
          </app-button>
        </div>
      </section>

      <div class="settings-divider"></div>

      <!-- Working time -->
      <section class="settings-section">
        <div class="settings-section__label">
          <div class="text-sm-semibold text-gray-900">Radno vreme sale</div>
        </div>
        <div class="settings-section__content">
          <div class="working-time-fields">
            <app-field label="Vreme otvaranja" for="working-from">
              <div class="time-input">
                <app-clock class="time-input__icon" stroke="#ADB0B8" width="20px" height="20px"></app-clock>
                <app-calendar
                  [formCtrl]="fromCtrl"
                  formCtrlId="working-from"
                  [timeOnly]="true"
                  [ctrlShowTime]="true"
                  dateFormat=""
                  panelStyleClass="admin-settings-time-picker"
                  ctrlPlaceholder="08:00"
                  [hideErrors]="true"
                ></app-calendar>
              </div>
            </app-field>
            <app-field label="Vreme zatvaranja" for="working-to">
              <div class="time-input">
                <app-clock class="time-input__icon" stroke="#ADB0B8" width="20px" height="20px"></app-clock>
                <app-calendar
                  [formCtrl]="toCtrl"
                  formCtrlId="working-to"
                  [timeOnly]="true"
                  [ctrlShowTime]="true"
                  dateFormat=""
                  panelStyleClass="admin-settings-time-picker"
                  ctrlPlaceholder="20:00"
                  [hideErrors]="true"
                ></app-calendar>
              </div>
            </app-field>
          </div>

          <div class="flex flex-column working-days">
            <div class="flex align-items-center justify-content-between">
              <div class="text-sm-medium text-gray-700">Radni dani</div>
              <div class="flex align-items-center working-days__count">
                <span class="text-sm-semibold text-gray-700">{{ selectedDaysCount() }}</span>
                <span class="text-sm-medium text-gray-500">od 7</span>
              </div>
            </div>

            <div class="day-pills">
              @for (day of DAYS; track day.value) {
                <button
                  type="button"
                  class="day-pill"
                  [class.day-pill--active]="isDaySelected(day.value)"
                  (click)="toggleDay(day.value)"
                >
                  {{ day.label }}
                </button>
              }
            </div>
          </div>
        </div>
      </section>

      <div class="settings-divider"></div>

      <!-- Date overrides -->
      <section class="settings-section">
        <div class="settings-section__label">
          <div class="text-sm-semibold text-gray-900">Neradni dani</div>
        </div>
        <div class="settings-section__content">
          @if (dateOverrides().length > 0) {
            <div class="overrides-list flex flex-column gap-3">
              @for (override of dateOverrides(); track $index) {
                <div class="override-row flex align-items-center justify-content-between">
                  <div class="flex align-items-center gap-3 override-main">
                    <div class="override-date-chip">
                      <div class="override-date-chip__month">
                        {{ formatOverrideMonth(override.date) }}
                      </div>
                      <div class="override-date-chip__day">
                        {{ formatOverrideDay(override.date) }}
                      </div>
                    </div>
                    <div class="flex flex-column override-text">
                      <div class="text-sm-medium text-gray-700">
                        {{ formatOverrideLabel(override.date) }}
                      </div>
                      <div class="text-sm-regular text-gray-500">
                        {{ formatOverrideIntervals(override) }}
                      </div>
                    </div>
                  </div>
                  <app-button
                    [btnSize]="ButtonSize.SM"
                    [btnSeverity]="ButtonSeverity.TERTIARY"
                    [isDestructive]="true"
                    btnClass="icon-only"
                    (btnClicked)="removeDateOverride($index)"
                  >
                    <app-trash-01 stroke="#F04438" width="20px" height="20px"></app-trash-01>
                  </app-button>
                </div>
              }
            </div>
          }

          <app-button
            [btnSeverity]="ButtonSeverity.LINK"
            (btnClicked)="addDateOverride()"
          >
            <app-plus stroke="#2970FF" width="20px" height="20px"></app-plus>
            <span>Dodaj neradan dan</span>
          </app-button>
        </div>
      </section>
    </div>

    <div class="settings-footer">
      <div class="settings-divider"></div>
      <div class="flex justify-content-end">
        <app-button
          [btnSize]="ButtonSize.MD"
          [btnSeverity]="ButtonSeverity.PRIMARY"
          (btnClicked)="save()"
        >
          <div class="text-sm-semibold text-white">Sačuvaj izmene</div>
        </app-button>
      </div>
    </div>
  </div>

  <app-invite-admins-dialog
    [(isVisible)]="inviteDialogVisible"
    (submitted)="onInviteSubmitted($event)"
  ></app-invite-admins-dialog>

  <app-date-override-dialog
    [(isVisible)]="dateOverrideDialogVisible"
    [existingOverrides]="dateOverrides()"
    (submitted)="onDateOverrideSubmitted($event)"
  ></app-date-override-dialog>

  <app-confirm-action-dialog
    [isVisible]="!!adminToDeactivate()"
    (isVisibleChange)="!$event && adminToDeactivate.set(undefined)"
    title="Deaktivirajte admina"
    [message]="'Da li ste sigurni da želite da deaktivirate admina ' + (adminToDeactivate()?.email ?? '') + '? Neće moći da se prijavi dok mu se pristup ponovo ne omogući.'"
    confirmLabel="Deaktiviraj"
    (confirmed)="confirmDeactivateAdmin()"
    (cancelled)="adminToDeactivate.set(undefined)"
  ></app-confirm-action-dialog>
</app-page>
